import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import styles from "./SharePointSettingsPage.module.less";
import { IValueInputAttachable } from "client/Forms/attachable";
import { SiteAvailabilityFormData } from "./SharePointSettingsPage";
import {
	SharePointSettingsNode,
	SharePointSettingsNodeId,
	SiteAvailabilityFilterKind
} from "client/Api/SharePointSettings/sharepointSettings";
import {
	AlertDialog,
	BulletList,
	Checkbox,
	FormItem,
	FormattedText,
	InputWithMoreInfo,
	RadioButtonGroup,
	RadioButtonGroupItem,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { generateTrackingId } from "client/Misc/utils";
import {
	HierarchySelection,
	SharePointIdMap,
	UINode,
	computeNodeIds,
	computeNodeMap
} from "./HierarchySelection";

export type SiteAvailabilityTabProps = {
	attachable: IValueInputAttachable<SiteAvailabilityFormData>;
	nodes: OptionalModel<readonly SharePointSettingsNode[]>;
	onLoadSiteHierarchy: () => void;
};

export const SiteAvailabilityTab = (props: SiteAvailabilityTabProps): ReactElement => {
	const {
		attachable,
		nodes,
		onLoadSiteHierarchy
	} = props;

	const [isMoreInfoOpen, setIsMoreInfoOpen] = useState<boolean>(false);

	const nodeMap = useMemo(
		(): SharePointIdMap<UINode> => {
			const {
				kind,
				nodeIds
			} = attachable.reducer
					.value;

			return computeNodeMap(
				kind,
				nodeIds,
				nodes
			);
		},
		[
			attachable.reducer
				.value,
			nodes
		]
	);

	const rootNodeIds = useMemo(
		(): readonly SharePointSettingsNodeId[] => {
			if (!nodes.isSet) {
				return [];
			}

			return nodes.value
				.map(x => x.id);
		},
		[nodes]
	);

	const changeNodeMapHandler = useCallback(
		(value: SharePointIdMap<UINode>): void => {
			const {
				kind
			} = attachable.reducer
					.value;

			const newNodeIds: readonly SharePointSettingsNodeId[] = computeNodeIds(
				rootNodeIds,
				kind,
				value
			);

			const nodeIdsAttachable: IValueInputAttachable<readonly SharePointSettingsNodeId[]>
				= attachable.for("nodeIds");

			nodeIdsAttachable
				.reducer
				.setValue(newNodeIds);

			nodeIdsAttachable.validation
				.triggerInputFinished();
		},
		[
			rootNodeIds,
			attachable
		]
	);

	const defaultAvailabilityChangeHandler = useCallback(
		(newValue: Exclude<SiteAvailabilityFilterKind, SiteAvailabilityFilterKind.None>): void => {

			const newNodeIds: readonly SharePointSettingsNodeId[] = computeNodeIds(
				rootNodeIds,
				newValue,
				nodeMap
			);

			const nodeIdsAttachable: IValueInputAttachable<readonly SharePointSettingsNodeId[]>
				= attachable.for("nodeIds");

			nodeIdsAttachable
				.reducer
				.setValue(newNodeIds);

			nodeIdsAttachable.validation
				.triggerInputFinished();
		},
		[
			attachable,
			rootNodeIds,
			nodeMap
		]
	);

	const isEnabledChangeHandler = useCallback(
		(newValue: boolean): void => {
			if (newValue && !nodes.isSet) {
				onLoadSiteHierarchy();
			}
		},
		[nodes.isSet, onLoadSiteHierarchy]
	);

	return (
		<div className={styles.tabContent}>
			<FormItem>
				<InputWithMoreInfo
					trackingId={generateTrackingId(
						attachable.for("isEnabled")
							.attachToCheckbox()
							.trackingId, "__more-info"
					)}
					onMoreInfo={() => setIsMoreInfoOpen(true)}
				>
					<Checkbox
						{...attachable.for("isEnabled")
							.attachToCheckbox()
							.withOnChange(isEnabledChangeHandler)
						}
						text={localize("_ControlSiteAvailability")}
						ellipseText={false}
					/>
				</InputWithMoreInfo>
				<ValidationError {...attachable.for("isEnabled").attachToValidationError()} />

				{isMoreInfoOpen && (
					<AlertDialog
						title={localize("_ControlSiteAvailability")}
						onClose={() => setIsMoreInfoOpen(false)}
						trackingId="site-availability-more-info-dialog"
						showCloseButton
					>
						<BulletList
							trackingId="points"
							rowGap="large"
						>
							<BulletList.Item>
								<FormattedText
									text={localize("_ControlSiteAvailabilityPart1")}
									thingsToReplace={[
										{ what: "{{ControlSiteAvailability}}", filler: <b>{localize("_ControlSiteAvailability")}</b> }
									]}
								/>
							</BulletList.Item>

							<BulletList.Item>
								<FormattedText
									text={localize("_ControlSiteAvailabilityPart2")}
									thingsToReplace={[
										{ what: "{{Available}}", filler: <b>{localize("_Available")}</b> }
									]}
								/>
							</BulletList.Item>

							<BulletList.Item>
								<FormattedText
									text={localize("_ControlSiteAvailabilityPart3")}
									thingsToReplace={[
										{ what: "{{NotAvailable}}", filler: <b>{localize("_NotAvailable")}</b> }
									]}
								/>
							</BulletList.Item>
						</BulletList>
					</AlertDialog>
				)}
			</FormItem>

			{attachable.for("isEnabled").value && (
				<>
					<FormItem
						label={localize("_DefaultAvailabilityOfSite")}
						isForGroup
					>
						<RadioButtonGroup
							{...attachable.for("kind")
								.attachToRadioButtonGroup()
								.withOnChange(defaultAvailabilityChangeHandler)
							}
							options={defaultOfficeDocumentEditorOptions}
						/>
						<ValidationError {...attachable.for("kind").attachToValidationError()} />
					</FormItem>

					<h3>{localize("_SiteOverview")}</h3>

					<ValidationError {...attachable.for("nodeIds").attachToValidationError()} />

					<HierarchySelection
						value={nodeMap}
						onChange={changeNodeMapHandler}
						rootNodeIds={rootNodeIds}
						trackingId={attachable.for("nodeIds").attachToCheckboxGroup().trackingId}
						onReload={onLoadSiteHierarchy}
					/>
				</>
			)}
		</div>
	);
};
SiteAvailabilityTab.displayName = "SiteAvailabilityTab";

const defaultOfficeDocumentEditorOptions: readonly RadioButtonGroupItem<SiteAvailabilityFilterKind>[] = [
	{
		text: localize("_NotAvailable"),
		value: SiteAvailabilityFilterKind.Whitelist
	},
	{
		text: localize("_Available"),
		value: SiteAvailabilityFilterKind.Blacklist
	}
];
